import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import {
  DataDetailToolbar,
  LoadingSpinner,
  DistrictsTableWithFilters,
} from '../../components';
import { useFeathers } from '../../app/util';

const ElectionGeodistricts = () => {
  const feathers = useFeathers();
  const history = useHistory();
  const location = useLocation();
  const { electionKey: electionKeyParam, key: keyParam } = useParams();
  const electionKey = electionKeyParam || keyParam;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [election, setElection] = useState(null);
  const [electionImplementation, setElectionImplementation] = useState(null);
  const [stateMatchName, setStateMatchName] = useState(null);

  const filterParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      page: searchParams.get('page') || '0',
      sort: searchParams.get('sort') || 'population:desc',
      shapefile: searchParams.get('shapefile'),
      childrenBoundaries: searchParams.get('childrenBoundaries'),
      childrenDistricts: searchParams.get('childrenDistricts'),
      population: searchParams.get('population'),
      search: searchParams.get('search') || '',
    };
  }, [location.search]);

  const onSetFilterParams = useCallback((updates) => {
    const searchParams = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
    });
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : ''
    });
  }, [history, location.pathname, location.search]);

  const geodistrictsTotal = electionImplementation?.aggregatedInformation?.geodistrictsTotal;
  const geodistrictsCollected = electionImplementation?.aggregatedInformation?.geodistrictsCollected;
  const completionPercent = useMemo(() => {
    if (!geodistrictsTotal) return 0;
    return Math.round((geodistrictsCollected / geodistrictsTotal) * 100);
  }, [geodistrictsCollected, geodistrictsTotal]);

  useEffect(() => {
    if (!feathers || !electionKey) return;

    const loadCoreData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [electionData, implementationData] = await Promise.all([
          feathers.getService('elections').get(electionKey),
          feathers.getService('election-implementations').get(electionKey),
        ]);
        const stateDistrict = electionData?.state
          ? await feathers.getService('districts').get(electionData.state)
          : null;
        setElection(electionData);
        setElectionImplementation(implementationData);
        setStateMatchName(stateDistrict?.matchName || null);
      } catch (err) {
        console.error(err);
        setError(err?.message || 'Failed to load election geodistrict data.');
      } finally {
        setLoading(false);
      }
    };

    loadCoreData();
  }, [feathers, electionKey]);

  if (loading) {
    return (
      <Wrapper>
        <LoadingTile>
          <LoadingSpinner scale={0.8} />
        </LoadingTile>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: { pathname: `/elections/${electionKey}` } }}
        navTree={[
          { text: 'Elections', to: { pathname: '/elections' } },
          { text: election?.name || electionKey, to: { pathname: `/elections/${electionKey}` } },
          { text: 'Geodistricts' },
        ]}
      />
      {error && (
        <Typography variant={'body1'} color={'error'} style={{ marginBottom: 12 }}>
          {error}
        </Typography>
      )}

      <DistrictsTableWithFilters
        baseQuery={{ election: electionKey }}
        stateMatchName={stateMatchName}
        filterParams={filterParams}
        onSetFilterParams={onSetFilterParams}
        defaultSort="population:desc"
        allowBulkDelete={false}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  min-height: calc(100% - 24px * 2);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const Header = styled.div`
  margin: 20px 0 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LoadingTile = styled.div`
  width: 100%;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default ElectionGeodistricts;
